/**
 * Descarga de archivos remotos al equipo del usuario.
 */

import { buildDisplayAddress, slugify } from './publicationCaption';

/**
 * Descarga un archivo remoto y lo guarda con el nombre indicado.
 *
 * Un <a href download> apuntando directo al CDN no sirve: el atributo download
 * se ignora cuando la URL es de otro origen, así que el navegador abre la
 * imagen en vez de bajarla. Por eso el fetch a blob.
 *
 * Dos detalles que parecen de más y no lo son:
 * - El fetch va sin headers propios. Los módulos de src/api mandan
 *   Authorization, X-Tenant y Content-Type; cualquiera de esos convierte el
 *   pedido en preflighted y el CDN no responde el OPTIONS.
 * - El revokeObjectURL va diferido. Revocarlo justo después del click corre
 *   carrera con el arranque de la descarga y deja archivos de 0 bytes.
 *
 * @param {string} url
 * @param {string} filename
 * @returns {Promise<void>} rechaza si el archivo no se pudo bajar
 */
export async function downloadRemoteFile(url, filename) {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) throw new Error('No se pudo descargar el archivo');

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
}

/**
 * Nombre de archivo para una foto de la propiedad.
 *
 * Usa la dirección publicable, nunca property.address: si la propiedad tiene
 * activado "No mostrar la dirección exacta", la calle real tampoco puede
 * terminar en el nombre del archivo que se sube a la red.
 *
 * No se usa asset.originalFilename porque PropertyAssets lo sanitiza
 * reemplazando el punto de la extensión por un guión bajo (foto_1_jpg).
 *
 * @param {?Object} property
 * @param {?Object} asset
 * @param {number} index posición de la foto en la grilla, base 0
 * @returns {string}
 */
export function buildPhotoFilename(property, asset, index) {
  const base =
    slugify(buildDisplayAddress(property)) ||
    slugify(property?.city) ||
    `propiedad-${property?.id || ''}`;

  const match = /\.([a-z0-9]+)(?:\?.*)?$/i.exec(asset?.path || '');
  const extension = match ? match[1].toLowerCase() : 'jpg';

  return `${base}-${index + 1}.${extension}`;
}

export default { downloadRemoteFile, buildPhotoFilename };
